// Single source of truth for shop / category-tree route paths and
// category-tree filtering rules. Portal content-page paths will live in
// app/utils/portalRoutes.ts (added under AB#60084). See docs/migration-rules.md §5.
//
// Ported from nuxt2 utils/categoryRoutes.ts.

// ─── Shop ────────────────────────────────────────────────────────────────
export const SHOP_ROOT = '/shop-products' as const
export const SHOP_ALL_PRODUCTS = '/shop-products/all-products' as const
export const SHOP_BY = '/shop-products/shop-by' as const
export const SHOP_BY_COLOR = '/shop-products/shop-by/color' as const
// Grouping nodes under /shop-products that have hasContent:true but no
// products of their own — used in SubcategoryThumbnails to skip the
// thumbnails grid on these pages.
export const SHOP_BY_CATEGORY_ROOT = '/shop-products/shop-by-category' as const
export const SHOP_COLOR_ROOT = '/shop-products/color' as const

// ─── Departments ─────────────────────────────────────────────────────────
// Department landing-page slugs. The per-department product-list filter
// configuration (utils/departmentFilters.ts) is keyed by these. AB#60142
export const SHOP_ADHESIVES = '/shop-products/adhesives' as const
export const SHOP_BRANDED_MERCHANDISE = '/shop-products/branded-merchandise' as const
export const SHOP_BUNDLED_SAVINGS = '/shop-products/bundled-savings' as const
export const SHOP_CRAFT_CLASSES = '/shop-products/craft-classes' as const
export const SHOP_DIE_CUTTING = '/shop-products/die-cutting' as const
export const SHOP_EMBELLISHMENTS_RIBBON = '/shop-products/embellishments-ribbon' as const
export const SHOP_EMBOSSING = '/shop-products/embossing' as const
export const SHOP_HOT_FOIL = '/shop-products/hot-foil' as const
export const SHOP_INK_COLORING = '/shop-products/ink-coloring' as const
export const SHOP_KITS_COLLECTION = '/shop-products/kits-collection' as const
export const SHOP_MEMORY_KEEPING = '/shop-products/memory-keeping' as const
export const SHOP_ONLINE_EXCLUSIVES = '/shop-products/online-exclusives' as const
export const SHOP_PAPER_PACKAGING = '/shop-products/paper-packaging' as const
export const SHOP_PUNCHES = '/shop-products/punches' as const
export const SHOP_REFILLS_PAST_KITS_ADD_ONS = '/shop-products/refills-past-kits-add-ons' as const
export const SHOP_STAMPS = '/shop-products/stamps' as const
export const SHOP_TOOLS_STORAGE = '/shop-products/tools-storage' as const

// Bare segment used in substring/regex normalization (e.g. stripping the
// /shop-by/ grouping segment out of an arrived-via-shop-by URL before
// looking it up in the category tree).
export const SHOP_BY_SEGMENT = '/shop-by' as const

// ─── Deals / What's Happening ────────────────────────────────────────────
// Section landing routes. Also matched as substrings of a product's category
// paths to exclude these promotional grouping branches when resolving the
// canonical category (see app/pages/products/[title]/index.vue).
export const DEALS_ROOT = '/deals' as const
export const WHATS_HAPPENING_ROOT = '/whats-happening' as const

// ─── Product of the Month ────────────────────────────────────────────────
// PoTM lives at /product-of-the-month for both the customer-facing URL
// and the product-data category tag. The legacy /specials/... slug has
// been retired backend-side.
export const PRODUCT_OF_THE_MONTH = '/product-of-the-month' as const

// ─── Sparkit ─────────────────────────────────────────────────────────────
// The category slug gates navigation visibility; the public CMS landing page
// remains a separate top-level route.
export const SPARKIT_CATEGORY = '/shop-products/sparkit' as const
export const SPARKIT_ROOT = '/sparkit' as const
// CMS content keys, not route paths — no leading slash, since
// navigationStore.fetchHtmlContent adds the `~/` prefix itself.
export const SPARKIT_CONTENT_KEY = 'sparkit' as const
// The shared band shown on every SparkIt product detail page. Authored as a
// child of the landing page's node, so the key is multi-segment — deliberate,
// and not interchangeable with SPARKIT_CONTENT_KEY above. AB#62455
export const SPARKIT_PDP_CONTENT_KEY = `${SPARKIT_CONTENT_KEY}/sparkit-pdp` as const

export const SPARKIT_AGES_6 = `${SPARKIT_CATEGORY}/ages-6` as const
export const SPARKIT_AGES_9 = `${SPARKIT_CATEGORY}/ages-9` as const
export const SPARKIT_SUPPLIES = `${SPARKIT_CATEGORY}/sparkit-supplies` as const

// Breadcrumb labels for the SparkIt shop routes come from these keys rather
// than the category tree. Sparkit Supplies is deliberately absent — design
// specified no key for it, so its crumb follows the route like any other
// subcategory.
export const SPARKIT_BREADCRUMB_KEYS: Readonly<Record<string, string>> = {
  [SPARKIT_CATEGORY]: 'sparkit.breadcrumb.allProducts',
  [SPARKIT_AGES_6]: 'sparkit.breadcrumb.ages6',
  [SPARKIT_AGES_9]: 'sparkit.breadcrumb.ages9'
}

// True for the SparkIt category and anything beneath it. The trailing-slash
// test keeps sibling categories that merely share the prefix (a hypothetical
// /shop-products/sparkit-clearance) from matching.
export const isSparkitShopPath = (path: string): boolean =>
  path === SPARKIT_CATEGORY || path.startsWith(`${SPARKIT_CATEGORY}/`)

// True when a category-tree node is the SparkIt category.
//
// Sitefinity authors category slugs with its `~` site-root marker, so the node
// arrives as `~/shop-products/sparkit` while the route the customer visits is
// `/shop-products/sparkit`. SPARKIT_CATEGORY stays route-shaped because route
// matching depends on it, so tree-slug comparisons go through here instead of
// testing equality against the constant directly. Only a single leading `~` is
// tolerated — anything else is a different category. AB#62408
export const isSparkitCategorySlug = (slug: string | undefined): boolean =>
  (slug ?? '').replace(/^~/, '') === SPARKIT_CATEGORY

// True when the product itself is a SparkIt product, whatever else it is filed
// under. Cross-merchandising is handled at the product level, so this — not the
// route the customer arrived through — is what drives SparkIt product-page
// behaviour. Non-string entries are dropped rather than thrown on: category
// arrays can arrive with a numeric id (AB#60767).
export const isSparkitProduct = (categorySlugs: readonly string[] | undefined): boolean =>
  (categorySlugs ?? []).some(s => typeof s === 'string' && isSparkitShopPath(s))

// ─── Special category-content segments ───────────────────────────────────
// Matched as substrings of a category path to detect promotional branches
// (see app/pages/shop-products/[...slug].vue and categories/[...slug].vue).
export const LIMITED_ACCESS_SEGMENT = '/limited-access' as const
export const LAST_CHANCE_SEGMENT = '/last-chance-products' as const

// ─── Core categories ─────────────────────────────────────────────────────
// The only categories a product breadcrumb may resolve to, in the business's
// priority order (agreed 2026-09-22, AB#63832). When a product sits in more
// than one, the earliest entry here wins. Anything not listed — Ways to Craft,
// Memory Keeping, campaign branches such as Flash Sale or the Gift Guide — is
// never a breadcrumb, however the product is filed. Ported from nuxt2
// utils/categoryRoutes.ts (AB#63978).
//
// Order notes:
//   - Product of the Month takes top precedence: while a product is the
//     monthly feature, that is what the shopper came for. It is the one core
//     category outside /shop-products — its route and its product tag are both
//     /product-of-the-month — which the prefix test handles unchanged.
//   - SparkIt is next, so a cross-merchandised SparkIt product keeps the
//     SparkIt trail whatever else it is filed under (AB#62455).
//   - Hot Foil follows because hot foil products are nearly always filed under
//     a second core category too; anywhere lower and a hot foil product reads
//     as the other category instead.
//   - Craft Classes, Kits Collection, Bundled Savings and Refills, Past Kits
//     & Add-Ons come ahead of the individual supply departments: a class, kit,
//     bundle or refill is what the customer bought, and the supply department
//     it also sits in is a component of it (AB#63940).
export const CORE_CATEGORY_ROOTS = [
  PRODUCT_OF_THE_MONTH,
  SPARKIT_CATEGORY,
  SHOP_HOT_FOIL,
  SHOP_CRAFT_CLASSES,
  SHOP_KITS_COLLECTION,
  SHOP_BUNDLED_SAVINGS,
  SHOP_REFILLS_PAST_KITS_ADD_ONS,
  SHOP_PAPER_PACKAGING,
  SHOP_DIE_CUTTING,
  SHOP_STAMPS,
  SHOP_EMBELLISHMENTS_RIBBON,
  SHOP_INK_COLORING,
  SHOP_EMBOSSING,
  SHOP_ADHESIVES,
  SHOP_PUNCHES,
  SHOP_TOOLS_STORAGE,
  SHOP_BRANDED_MERCHANDISE
] as const

// Grouping segments from the category structure that predates the nav
// restructure. Products still tagged only under them (e.g.
// /shop-products/other-products/die-cutting) belong to the core category
// beneath the grouping, so breadcrumb resolution reads through these. None of
// them is a live route — the breadcrumb always links the current core path.
export const LEGACY_CATEGORY_GROUPINGS = [
  'featured-products',
  'other-products'
] as const

// ─── Filtering / exclusions ──────────────────────────────────────────────

// Slug segments excluded from the sidebar category list. These are
// grouping/synthetic categories that shouldn't appear as user-clickable
// filters even though they exist in the category tree.
export const SIDEBAR_EXCLUDED_SLUGS = [
  'all-products',
  'shop-by-category',
  'shop-by-release',
  'deals',
  'sparkit',
  'subscriptions'
] as const

// Slug prefixes excluded from breadcrumb chains. /shop-products/shop-by is
// a nav grouping wrapper, not a real category branch — skip it when
// reconstructing the chain from a slug.
export const BREADCRUMB_EXCLUDED_SEGMENTS = [
  SHOP_BY
] as const
